import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const FACILITATOR_STEPS = [
  {
    emoji: '🃏',
    title: 'Welcome to Planning Poker',
    body: 'Estimate effort as a team and write story points directly to Jira — no copy-paste.'
  },
  {
    emoji: '👥',
    title: 'Create a team',
    body: 'From the home page, click "New Team", give it a name, and link it to a Jira project.'
  },
  {
    emoji: '🚀',
    title: 'Start a session',
    body: "Click \"Start Session\" on any team card. You're the facilitator — you control the flow."
  },
  {
    emoji: '🗂️',
    title: 'Pick tickets & queues',
    body: 'Use "Pick Ticket" to pull from Jira. Build a queue to estimate multiple tickets in sequence without stopping between rounds.'
  },
  {
    emoji: '🗳️',
    title: 'Run a round',
    body: "Participants vote in secret. Click \"Reveal Cards\" when ready. Re-vote if there's no consensus, or set the final points and save to Jira in one click."
  },
  {
    emoji: '📊',
    title: 'Reports & baselines',
    body: 'View full session history in Reports. Save well-understood tickets as Baselines — they show up during estimation as a reference point.'
  }
];

const PARTICIPANT_STEPS = [
  {
    emoji: '👋',
    title: 'Welcome',
    body: "You've been invited to a planning poker session. Here's how it works."
  },
  {
    emoji: '🔗',
    title: 'Joining a session',
    body: 'Paste a session link or ID into the home page. No account needed — just enter your name.'
  },
  {
    emoji: '🃏',
    title: 'Voting',
    body: "Pick a card to cast your estimate. Use ☕ if the ticket needs more discussion first. Use ∞ if it's too large and should be broken down. Cards stay hidden until the facilitator reveals them."
  }
];

export default function TourModal({ onClose }) {
  const { user } = useAuth();
  const steps = user ? FACILITATOR_STEPS : PARTICIPANT_STEPS;

  const [step, setStep] = useState(0);
  const [dontShow, setDontShow] = useState(false);

  const isLast = step === steps.length - 1;
  const current = steps[step];

  const handleDone = () => {
    localStorage.setItem('pp_tour_done', '1');
    onClose();
  };

  const handleDismiss = () => {
    if (dontShow) localStorage.setItem('pp_tour_done', '1');
    onClose();
  };

  return (
    <div className="tour-overlay" onClick={e => e.target === e.currentTarget && handleDismiss()}>
      <div className="tour-modal">
        <button
          className="btn btn-ghost btn-icon tour-close-btn"
          onClick={handleDismiss}
        >✕</button>

        <span className="tour-icon">{current.emoji}</span>
        <h3>{current.title}</h3>
        <p>{current.body}</p>

        <div className="tour-dots">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`tour-dot${i <= step ? ' tour-dot--active' : ''}`}
            />
          ))}
        </div>

        <div className="tour-actions">
          {isLast ? (
            <label className="tour-dont-show">
              <input
                type="checkbox"
                checked={dontShow}
                onChange={e => setDontShow(e.target.checked)}
              />
              Don't show again
            </label>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setStep(s => s - 1)}>
                ← Prev
              </button>
            )}
            {isLast ? (
              <button className="btn btn-primary btn-sm" onClick={handleDone}>
                Done ✓
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => setStep(s => s + 1)}>
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
